import { actions, config, helpers, mui, React, ts, useDispatch, useLocation, useSelector } from '_core';

import ScheduleDemoModal from '_components/schedule-demo-modal';
import TourItem from '_components/tour-item';

import PrimaryNavLink from './primary-nav-link';
import ApiDocsModal from '../api/docs';

type PrimaryNavProps = {
  dark?: boolean;
};

const PrimaryNav = ({ dark }: PrimaryNavProps) => {
  const dispatch = useDispatch();

  const location = useLocation();

  const organization = useSelector((state) => state.auth.organization);
  const currentTab = useSelector((state) => state.ui.currentTab);

  const setCurrentTab = (tab: ts.types.common.CurrentTab) => dispatch(actions.ui.setCurrentTab(tab));
  const setScheduleDemoOpen = (o: boolean) => dispatch(actions.ui.setScheduleDemoOpen(o));

  const isFinseraUser = organization?.id === 0;

  const routeChangeTriggeredRef = React.useRef<[string, string]>(null);
  const locRef = React.useRef(location.pathname);
  // eslint-disable-next-line react-compiler/react-compiler
  locRef.current = location.pathname;

  // On first load, we will check which is the current tab
  React.useEffect(() => {
    // After route changed and path was updated we can check if we need to update the current tab
    if (routeChangeTriggeredRef.current && routeChangeTriggeredRef.current[1] !== locRef.current) {
      setCurrentTab(routeChangeTriggeredRef.current[0] as ts.types.common.CurrentTab);
      // eslint-disable-next-line react-compiler/react-compiler
      routeChangeTriggeredRef.current = null;
    }

    const currentTabLocal = helpers.routes.getCurrentTab(locRef.current);
    if (currentTabLocal !== currentTab) {
      setCurrentTab(currentTabLocal as ts.types.common.CurrentTab);
    }
  }, [location.pathname]);

  const HandleUserLink = (tabName: ts.types.common.CurrentTab) => {
    if (currentTab !== tabName) {
      routeChangeTriggeredRef.current = [tabName, locRef.current];
    }
  };

  return (
    <>
      <PrimaryNavLink
        id="dashboard-link"
        to="/"
        onClick={() => HandleUserLink('dashboard')}
        selected={currentTab == 'dashboard'}
        icon={<mui.icons.HomeOutlined />}
        tooltip={'Dashboard'}
        dark={dark}
      />

      <PrimaryNavLink
        id="research-link"
        to="/research"
        onClick={() => HandleUserLink('research')}
        selected={currentTab == 'research'}
        icon={<mui.icons.DesktopMacOutlined style={{ fontSize: '0.9rem' }} />}
        tooltip={'Research'}
        disabledTooltip={
          <TourItem
            name="Research"
            description="Develop and organize your strategies and render visual reports for your clients"
            demoAction={() => setScheduleDemoOpen(true)}
          />
        }
        dark={dark}
      />
      {config.features.data_browser && (
        <PrimaryNavLink
          id="datasets-link"
          to="/datasets"
          onClick={() => HandleUserLink('datasets')}
          selected={currentTab == 'datasets'}
          icon={<mui.icons.StorageOutlined />}
          tooltip={'Data Sources'}
          disabledTooltip={
            <TourItem
              name="Data Sources"
              description="You can go to this area and navigate to your subscribed and custom data
                    sets in order to preview the raw data."
              demoAction={() => setScheduleDemoOpen(true)}
            />
          }
          dark={dark}
        />
      )}
      {config.features.production_mode && (
        <PrimaryNavLink
          id="production-link"
          to="/production"
          onClick={() => HandleUserLink('production')}
          selected={currentTab == 'production'}
          icon={<mui.icons.RocketLaunchOutlined />}
          tooltip={'Production'}
          disabledTooltip={
            <TourItem
              name="Data Sources"
              description="Once your strategy is ready you can go to this area
                    to start working with real investments and trading."
              demoAction={() => setScheduleDemoOpen(true)}
            />
          }
          dark={dark}
        />
      )}
      {config.features.publish_source && (
        <PrimaryNavLink
          id="publsh-link"
          to="/publish"
          onClick={() => HandleUserLink('publish')}
          selected={currentTab == 'publish'}
          icon={<mui.icons.PublishedWithChangesOutlined />}
          tooltip={'Publish'}
          dark={dark}
        />
      )}
      {isFinseraUser && (
        <PrimaryNavLink
          id="admin-link"
          to="/finsera-admin"
          onClick={() => HandleUserLink('finsera-admin')}
          selected={currentTab == 'finsera-admin'}
          icon={<mui.icons.SettingsOutlined />}
          tooltip={'Admin'}
          dark={dark}
        />
      )}
      {config.features.recent_jobs && (
        <PrimaryNavLink
          id="recent-link"
          to="/reports"
          onClick={() => HandleUserLink('jobber')}
          selected={currentTab == 'jobber'}
          icon={<mui.icons.BarChartOutlined />}
          tooltip={'Recent Reports'}
          disabledTooltip={
            <TourItem
              name="Reports"
              description="This area shows jobs (reports or performance charts) you've recently
                    run and their completion status.
                  This is a convenient place to see which of your jobs are still running and which have completed
                  and to navigate quickly to the results."
              demoAction={() => setScheduleDemoOpen(true)}
            />
          }
          dark={dark}
        />
      )}
      {dark && <mui.core.Divider />}
      <ScheduleDemoModal />
      <ApiDocsModal />
    </>
  );
};

export default PrimaryNav;
